import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Mail } from 'lucide-react';
import log from 'loglevel';
import { useSearchParams } from 'react-router-dom';

import { useToast } from '@admin-platform/ui/components';

import { Alert, AlertDescription } from '../../Alert/Alert';
import { Badge } from '../../Badge/Badge';
import { Button } from '../../Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Card/Card';
import Checkbox from '../../Checkbox/Checkbox';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import { useTenantFeatures } from '../../../hooks/useTenantFeatures';
import { TenantFeature } from '../../../types/tenant.types';
import { parseGraphQLErrorForUser } from '../../../utils/normalizeGraphqlError';

import type {
  GmailIntegrationQuery,
  GmailIntegrationQueryVariables,
} from './queries/gmailIntegration.query.graphql';
import GMAIL_INTEGRATION from './queries/gmailIntegration.query.graphql';

import type {
  GmailAuthUrlQuery,
  GmailAuthUrlQueryVariables,
} from './queries/gmailAuthUrl.query.graphql';
import GMAIL_AUTH_URL from './queries/gmailAuthUrl.query.graphql';

import type {
  DisconnectGmailMutation,
  DisconnectGmailMutationVariables,
} from './mutations/disconnectGmail.mutation.graphql';
import DISCONNECT_GMAIL from './mutations/disconnectGmail.mutation.graphql';

import styles from './GmailSettings.module.css';

/**
 * Per-user Gmail mailbox. Lives on Account, not tenant Settings, because
 * each person connects their own inbox and sends as themselves. Drive folder
 * sync stays under Settings → Integrations (tenant-level).
 *
 * Hidden without CRM: Conversation is the only surface that uses the mailbox,
 * and the connect API is gated on the same flag.
 */
export default function GmailSettings(): JSX.Element | null {
  const toast = useToast();
  const { hasFeature } = useTenantFeatures();
  const crmEnabled = hasFeature(TenantFeature.CRM);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [deleteEmails, setDeleteEmails] = useState(false);
  const handledCallbackRef = useRef<string | null>(null);

  useEffect(() => {
    const gmailParam = searchParams.get('gmail');
    if (!gmailParam) {
      return;
    }
    // StrictMode re-runs this effect on the same params before setSearchParams
    // flushes, which would otherwise toast twice.
    const key = `${gmailParam}:${searchParams.get('email') ?? ''}:${searchParams.get('reason') ?? ''}`;
    if (handledCallbackRef.current === key) {
      return;
    }
    handledCallbackRef.current = key;

    if (gmailParam === 'success') {
      const email = searchParams.get('email');
      toast(email ? `Gmail connected as ${email}` : 'Gmail connected', 'success');
    } else if (gmailParam === 'error') {
      const reason = searchParams.get('reason') || 'Unknown error';
      toast(
        reason === 'access_denied'
          ? 'Gmail connection cancelled'
          : 'Failed to connect Gmail: ' + reason,
        'error',
      );
    }
    setSearchParams({});
  }, [searchParams, setSearchParams, toast]);

  const { data, loading, error, refetch } = useQuery<
    GmailIntegrationQuery,
    GmailIntegrationQueryVariables
  >(GMAIL_INTEGRATION, { fetchPolicy: 'cache-and-network', skip: !crmEnabled });

  const [fetchAuthUrl, { loading: authUrlLoading }] = useLazyQuery<
    GmailAuthUrlQuery,
    GmailAuthUrlQueryVariables
  >(GMAIL_AUTH_URL, { fetchPolicy: 'network-only' });

  // Apollo Client 4 dropped the lazy query callbacks; the executor's promise carries the outcome.
  const startGmailAuth = async (): Promise<void> => {
    try {
      const { data: result, error: authError } = await fetchAuthUrl();
      if (authError) throw authError;
      if (result?.gmailAuthUrl?.url) {
        window.location.href = result.gmailAuthUrl.url;
      }
    } catch (err) {
      log.error('Error fetching Gmail auth URL:', err);
      toast(parseGraphQLErrorForUser(err), 'error');
    }
  };

  const [disconnectGmail, { loading: disconnectLoading }] = useMutation<
    DisconnectGmailMutation,
    DisconnectGmailMutationVariables
  >(DISCONNECT_GMAIL, {
    onCompleted: () => {
      refetch();
      setShowDisconnectModal(false);
      setDeleteEmails(false);
      toast('Gmail disconnected', 'success');
    },
    onError: (err) => {
      log.error('Error disconnecting Gmail:', err);
      toast(parseGraphQLErrorForUser(err), 'error');
    },
  });

  const isConnected = data?.gmailIntegration?.connected ?? false;
  const senderEmail = data?.gmailIntegration?.senderEmail;

  if (!crmEnabled) {
    return null;
  }

  return (
    <Card className={styles.Card} noHover>
      <CardHeader>
        <div className={styles.Header}>
          <span className={styles.HeaderIcon} aria-hidden="true">
            <Mail />
          </span>
          <div className={styles.HeaderText}>
            <CardTitle>Gmail</CardTitle>
            <CardDescription>
              Connect your mailbox to send and receive client email. Colleagues connect their own.
              Drive folders stay in Settings.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Content}>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load your Gmail connection.{' '}
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : loading && !data ? (
          <p className={styles.Status}>Loading…</p>
        ) : !isConnected ? (
          <div className={styles.Actions}>
            <Button variant="primary" onClick={() => void startGmailAuth()} disabled={authUrlLoading}>
              {authUrlLoading ? 'Redirecting…' : 'Connect Gmail'}
            </Button>
          </div>
        ) : (
          <>
            <div className={styles.StatusRow}>
              <Badge badgeType="outline" indicator="green">
                Connected
              </Badge>
              {senderEmail && <span className={styles.SendingAs}>{senderEmail}</span>}
            </div>
            <div className={styles.Actions}>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setDeleteEmails(false);
                  setShowDisconnectModal(true);
                }}
                disabled={disconnectLoading}
              >
                {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      {showDisconnectModal && (
        <ConfirmationModal
          onClose={() => {
            setShowDisconnectModal(false);
            setDeleteEmails(false);
          }}
        >
          <ConfirmationModal.Body>
            <p>
              Disconnect your Gmail mailbox? Revvly will stop sending and receiving mail through it.
            </p>
            <label className={styles.DeleteEmails}>
              <Checkbox
                checked={deleteEmails}
                onChange={(e) => setDeleteEmails(e.target.checked)}
                disabled={disconnectLoading}
              />
              <span>
                Also delete emails this mailbox sent or received from Revvly. Leave unchecked to
                keep them on client records.
              </span>
            </label>
          </ConfirmationModal.Body>
          <ConfirmationModal.Actions>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowDisconnectModal(false);
                setDeleteEmails(false);
              }}
              disabled={disconnectLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => disconnectGmail({ variables: { deleteEmails } })}
              disabled={disconnectLoading}
            >
              {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </ConfirmationModal.Actions>
        </ConfirmationModal>
      )}
    </Card>
  );
}
